import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { getTenantToday } from '../utils/dateUtils';
import { fetchClassifications, type SecurityClassification } from './classificationPivot';
import {
  Lock,
  Building2,
  Briefcase,
  BarChart3,
  TrendingUp,
  ArrowDownToLine,
  LineChart,
  Layers3,
  Trophy,
  Users,
  ChevronDown,
  Landmark,
  Radar
} from 'lucide-react';

// Import existing dashboards
import PrivateAssetDashboard from './PrivateAssetDashboard';
import PrivateCreditHome from '../features/privateCredit/PrivateCreditHome';
import RealEstateDashboard from './RealEstateDashboard';
import RealEstateDebtScreen from './credit/RealEstateDebtScreen';
import CommitmentDashboard from './CommitmentDashboard';
import PortfolioCompaniesWorkbench from './alternatives/PortfolioCompaniesWorkbench';
import PEDistributionsPanel from './alternatives/PEDistributionsPanel';
import CashFlowForecastScreen from './alternatives/CashFlowForecastScreen';
import FoFLookthroughPanel from './alternatives/FoFLookthroughPanel';
import LPPerformanceCenterScreen from './pe/LPPerformanceCenterScreen';
import LPFundPerformanceDetailScreen from './pe/LPFundPerformanceDetailScreen';
import GPLPRosterScreen from './gp/GPLPRosterScreen';
import { AxiomMetricCard, AxiomMetricGrid } from './shared';
import {
  CollapsedRail,
  CollapsedRailItem,
  CollapsedRailSection,
  Eyebrow,
  HeroSubtitle,
  HeroTitle,
  HeroTop,
  PanelBody,
  PanelHeader,
  PanelSubtitle,
  PanelTitle,
  RailChevron,
  RailItem,
  RailItemDescription,
  RailItemLabel,
  RailSection,
  RailSectionHeader,
  RailSectionLabel,
  RailSectionMeta,
  ViewPill,
  ViewSwitch,
  WorkspaceBody,
  WorkspaceHero,
  WorkspaceShell,
  WorkspaceMain,
  WorkspacePanel,
  WorkspaceRail,
  WorkspaceRailToggle,
} from './shared/Workspace';

type AlternativesSubTab = 'overview' | 'private-equity' | 'lp-performance' | 'lp-fund-detail' | 'pe-commitments' | 'pe-distributions' | 'lp-roster' | 'fof-lookthrough' | 'cashflow-forecast' | 'portfolio-companies' | 'real-estate' | 'private-credit' | 're-debt';
type AlternativesView = 'pm' | 'risk' | 'ops';
type AlternativesItem = {
  id: AlternativesSubTab;
  label: string;
  description: string;
  icon: React.ReactNode;
  tone?: 'blue' | 'cyan' | 'emerald' | 'amber' | 'red' | 'violet' | 'slate';
};

const formatCurrency = (value: number) => {
  if (value >= 1000000000) return `$${(value / 1000000000).toFixed(1)}B`;
  if (value >= 1000000) return `$${(value / 1000000).toFixed(0)}M`;
  return `$${(value ?? 0).toLocaleString()}`;
};

const AlternativesOverview: React.FC<{ onJumpToSubTab?: (tab: AlternativesSubTab) => void }> = ({ onJumpToSubTab }) => {
  const [positions, setPositions] = useState<any[]>([]);
  const [classifications, setClassifications] = useState<Map<number, SecurityClassification>>(new Map());
  const [portcoCount, setPortcoCount] = useState<number>(0);
  const [breachedCovenants, setBreachedCovenants] = useState<number>(0);

  useEffect(() => {
    // Same bi-temporal replay + canonical classification as the Holdings tab.
    // The legacy exact-date /api/positions read plus class-name string guesses
    // rendered every bucket $0 once canonical names went live (F-HUB-013).
    const today = getTenantToday();
    axios.get('/api/ibor/replay', { params: { validAsOf: today, knowledgeAsOf: 'now', basis: 'TRADE_DATE' } })
      .then(async res => {
        const rows = Array.isArray(res.data?.positions) ? res.data.positions : [];
        setPositions(rows);
        const ids = Array.from(new Set(rows.map((p: any) => p.securityId).filter(Boolean))) as number[];
        try {
          setClassifications(await fetchClassifications(ids, today, ''));
        } catch {
          setClassifications(new Map());
        }
      })
      .catch(() => {});

    axios.get('/api/portco')
      .then(res => setPortcoCount(Array.isArray(res.data) ? res.data.length : 0))
      .catch(() => {});

    axios.get('/api/portco/covenants/rollup/portco')
      .then(res => {
        const rows = Array.isArray(res.data) ? res.data : [];
        setBreachedCovenants(rows.filter((r: any) => r.severity === 'BREACHED').length);
      })
      .catch(() => {});
  }, []);

  const l1Of = (p: any) => classifications.get(p.securityId)?.l1Name || '';
  const pePositions = positions.filter((p: any) => l1Of(p) === 'Alternatives');
  const rePositions = positions.filter((p: any) => l1Of(p) === 'Real Assets');
  const pcPositions = positions.filter((p: any) => l1Of(p) === 'Private Credit');
  const privatePositions = [...pePositions, ...pcPositions];

  const sumMV = (arr: any[]) => arr.reduce((s, p) => s + (Number(p.marketValue) || 0), 0);
  const totalAUM = sumMV(privatePositions) + sumMV(rePositions);

  return (
    <div className="p-8">
      <AxiomMetricGrid style={{ marginBottom: '2rem' }}>
        <AxiomMetricCard label="Total Alternatives AUM" value={formatCurrency(totalAUM)} subtext="Across all strategies" icon={<Lock />} tone="violet" />
        <AxiomMetricCard label="Private Equity" value={pePositions.length} subtext={`${formatCurrency(sumMV(pePositions))} market value`} icon={<Briefcase />} tone="violet" />
        <AxiomMetricCard label="Real Estate" value={rePositions.length} subtext={`${formatCurrency(sumMV(rePositions))} market value`} icon={<Building2 />} tone="emerald" />
        <AxiomMetricCard label="Private Credit" value={pcPositions.length} subtext={`${formatCurrency(sumMV(pcPositions))} market value`} icon={<Landmark />} tone="cyan" />
        <AxiomMetricCard
          label="Portfolio Companies"
          value={portcoCount}
          subtext={breachedCovenants > 0 ? `${breachedCovenants} covenant breach${breachedCovenants === 1 ? '' : 'es'}` : 'covenants healthy'}
          icon={<Users />}
          tone={breachedCovenants > 0 ? 'red' : 'emerald'}
          onClick={() => onJumpToSubTab?.('portfolio-companies')}
        />
      </AxiomMetricGrid>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-slate-800/30 border border-white/5 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Lock className="w-5 h-5 text-violet-400" />
            Top Holdings by Market Value
          </h3>
          {privatePositions.length > 0 ? (
            <div className="space-y-3">
              {[...privatePositions].sort((a, b) => (b.marketValue || 0) - (a.marketValue || 0)).slice(0, 5).map((p: any, i: number) => (
                <div key={i} className="flex justify-between items-center">
                  <span className="text-slate-400 text-sm truncate mr-4">{p.securityName}</span>
                  <span className="text-white font-semibold text-sm whitespace-nowrap">{formatCurrency(p.marketValue || 0)}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">No private positions to display</div>
          )}
        </div>
        <div className="bg-slate-800/30 border border-white/5 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-emerald-400" />
            Real Estate Portfolio
          </h3>
          {rePositions.length > 0 ? (
            <div className="space-y-3">
              {rePositions.map((p: any, i: number) => (
                <div key={i} className="flex justify-between items-center">
                  <span className="text-slate-400 text-sm truncate mr-4">{p.securityName}</span>
                  <span className="text-white font-semibold text-sm whitespace-nowrap">{formatCurrency(p.marketValue || 0)}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">No real estate positions to display</div>
          )}
        </div>
      </div>
    </div>
  );
};

const AlternativesTab: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const subFromUrl = searchParams.get('sub') as AlternativesSubTab | null;
  const validSubs: AlternativesSubTab[] = [
    'overview', 'private-equity', 'lp-performance', 'lp-fund-detail', 'pe-commitments', 'pe-distributions',
    'lp-roster', 'fof-lookthrough', 'cashflow-forecast', 'portfolio-companies', 'real-estate',
    'private-credit', 're-debt',
  ];
  const initialSub: AlternativesSubTab =
    subFromUrl && validSubs.includes(subFromUrl) ? subFromUrl : 'overview';
  const [activeSubTab, setActiveSubTab] = useState<AlternativesSubTab>(initialSub);
  const [workspaceView, setWorkspaceView] = useState<AlternativesView>('pm');
  const [railOpen, setRailOpen] = useState({ strategies: true, operations: true });
  const [railCollapsed, setRailCollapsed] = useState(false);

  // Keep activeSubTab in sync if the URL `sub` param changes (e.g., back navigation).
  useEffect(() => {
    if (subFromUrl && validSubs.includes(subFromUrl) && subFromUrl !== activeSubTab) {
      setActiveSubTab(subFromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subFromUrl]);

  // Push subtab changes back to the URL so deep-links survive reloads / back nav.
  const handleSubTabChange = (next: AlternativesSubTab) => {
    setActiveSubTab(next);
    const params = new URLSearchParams(searchParams);
    params.set('sub', next);
    setSearchParams(params, { replace: true });
  };

  const viewConfig: Record<AlternativesView, {
    label: string;
    defaultTab: AlternativesSubTab;
    copy: string;
    strategyOrder: AlternativesSubTab[];
    operationsOrder: AlternativesSubTab[];
  }> = {
    pm: {
      label: 'PM View',
      defaultTab: 'overview',
      copy: 'Prioritises exposure, commitments, NAV, liquidity pacing and manager/fund performance.',
      strategyOrder: ['overview', 'private-equity', 'private-credit', 'real-estate', 're-debt'],
      operationsOrder: ['lp-performance', 'pe-commitments', 'pe-distributions', 'fof-lookthrough', 'cashflow-forecast', 'portfolio-companies', 'lp-roster'],
    },
    risk: {
      label: 'Risk View',
      defaultTab: 'portfolio-companies',
      copy: 'Prioritises portfolio-company health, cash-flow stress, look-through exposure and private-credit/RE downside.',
      strategyOrder: ['private-credit', 're-debt', 'private-equity', 'real-estate', 'overview'],
      operationsOrder: ['portfolio-companies', 'cashflow-forecast', 'fof-lookthrough', 'lp-performance', 'pe-commitments', 'pe-distributions', 'lp-roster'],
    },
    ops: {
      label: 'Ops View',
      defaultTab: 'pe-commitments',
      copy: 'Prioritises commitments, capital calls, distributions, LP roster, reporting cadence and operational reconciliation.',
      strategyOrder: ['overview', 'private-equity', 'real-estate', 'private-credit', 're-debt'],
      operationsOrder: ['pe-commitments', 'pe-distributions', 'cashflow-forecast', 'lp-roster', 'lp-performance', 'fof-lookthrough', 'portfolio-companies'],
    },
  };

  const strategyTabs: AlternativesItem[] = [
    { id: 'overview', label: 'Overview', description: 'Alternatives book command view', icon: <BarChart3 />, tone: 'violet' },
    { id: 'private-equity', label: 'Private Equity', description: 'LP interests, secondaries and PE holdings', icon: <Briefcase />, tone: 'violet' },
    { id: 'private-credit', label: 'Private Credit', description: 'Direct lending and private fixed-income holdings', icon: <Lock />, tone: 'cyan' },
    { id: 'real-estate', label: 'Real Estate Equity', description: 'Property-level equity NAV and operating views', icon: <Building2 />, tone: 'emerald' },
    { id: 're-debt', label: 'Real Estate Debt', description: 'Bridge, mezzanine, construction and whole-loan exposure', icon: <Landmark />, tone: 'amber' },
  ];

  const operationsTabs: AlternativesItem[] = [
    { id: 'lp-performance', label: 'LP Performance', description: 'IRR, TVPI, DPI and benchmarked fund performance', icon: <Trophy />, tone: 'violet' },
    { id: 'pe-commitments', label: 'Commitments', description: 'Capital commitments, unfunded and call schedule', icon: <Landmark />, tone: 'cyan' },
    { id: 'pe-distributions', label: 'Distributions', description: 'Distribution notices and cash return tracking', icon: <ArrowDownToLine />, tone: 'emerald' },
    { id: 'fof-lookthrough', label: 'FoF Look-through', description: 'Underlying fund and company exposure transparency', icon: <Layers3 />, tone: 'blue' },
    { id: 'cashflow-forecast', label: 'Cash-flow Forecast', description: 'Capital calls, distributions and liquidity pacing', icon: <LineChart />, tone: 'cyan' },
    { id: 'portfolio-companies', label: 'Portfolio Companies', description: 'Portco operating metrics, covenants and value bridge', icon: <Building2 />, tone: 'amber' },
    { id: 'lp-roster', label: 'LP Roster', description: 'Investor commitments, side letters and allocation context', icon: <Users />, tone: 'slate' },
  ];

  const hiddenDetailTabs: AlternativesItem[] = [
    { id: 'lp-fund-detail', label: 'LP Fund Detail', description: 'Fund-level performance, cash flows, reports and secondary pricing', icon: <Radar />, tone: 'violet' },
  ];

  const activeView = viewConfig[workspaceView];
  const orderTabs = (tabs: AlternativesItem[], order: AlternativesSubTab[]) => {
    const byId = new Map(tabs.map(tab => [tab.id, tab]));
    const ordered = order.map(id => byId.get(id)).filter(Boolean) as AlternativesItem[];
    const remaining = tabs.filter(tab => !order.includes(tab.id));
    return [...ordered, ...remaining];
  };
  const strategyTabsForView = orderTabs(strategyTabs, activeView.strategyOrder);
  const operationsTabsForView = orderTabs(operationsTabs, activeView.operationsOrder);
  const activeMeta = [...strategyTabs, ...operationsTabs, ...hiddenDetailTabs].find(tab => tab.id === activeSubTab) || strategyTabs[0];

  const handleWorkspaceViewChange = (next: AlternativesView) => {
    setWorkspaceView(next);
    handleSubTabChange(viewConfig[next].defaultTab);
  };

  const renderRailItems = (items: AlternativesItem[]) => items.map(tab => (
    <RailItem
      key={tab.id}
      $active={activeSubTab === tab.id}
      $tone={tab.tone || 'violet'}
      onClick={() => handleSubTabChange(tab.id)}
    >
      {tab.icon}
      <div>
        <RailItemLabel>{tab.label}</RailItemLabel>
        <RailItemDescription>{tab.description}</RailItemDescription>
      </div>
    </RailItem>
  ));

  const renderCollapsedRailItems = (items: AlternativesItem[]) => (
    <CollapsedRailSection>
      {items.map(tab => (
        <CollapsedRailItem
          key={tab.id}
          $active={activeSubTab === tab.id}
          $tone={tab.tone || 'violet'}
          aria-label={tab.label}
          title={`${tab.label} - ${tab.description}`}
          onClick={() => handleSubTabChange(tab.id)}
        >
          {tab.icon}
        </CollapsedRailItem>
      ))}
    </CollapsedRailSection>
  );

  return (
    <WorkspaceShell>
      <WorkspaceHero>
        <HeroTop>
          <div>
            <Eyebrow $tone="violet">Alternatives Workspace</Eyebrow>
            <HeroTitle>Alternatives Control Room</HeroTitle>
            <HeroSubtitle>
              One operating surface for private equity, private credit, real estate, commitments,
              distributions, cash-flow pacing, look-through exposure and portfolio-company oversight.
            </HeroSubtitle>
          </div>
          <ViewSwitch aria-label="Alternatives workspace view">
            <ViewPill $active={workspaceView === 'pm'} onClick={() => handleWorkspaceViewChange('pm')}>PM View</ViewPill>
            <ViewPill $active={workspaceView === 'risk'} onClick={() => handleWorkspaceViewChange('risk')}>Risk View</ViewPill>
            <ViewPill $active={workspaceView === 'ops'} onClick={() => handleWorkspaceViewChange('ops')}>Ops View</ViewPill>
          </ViewSwitch>
        </HeroTop>
        <HeroSubtitle>{activeView.copy}</HeroSubtitle>
      </WorkspaceHero>

      <WorkspaceBody $railCollapsed={railCollapsed}>
        <WorkspaceRail $collapsed={railCollapsed}>
          <WorkspaceRailToggle collapsed={railCollapsed} onToggle={() => setRailCollapsed(prev => !prev)} />
          {railCollapsed ? (
            <CollapsedRail>
              {renderCollapsedRailItems(strategyTabsForView)}
              {renderCollapsedRailItems(operationsTabsForView)}
            </CollapsedRail>
          ) : (
            <>
              <RailSection>
                <RailSectionHeader
                  type="button"
                  aria-expanded={railOpen.strategies}
                  onClick={() => setRailOpen(prev => ({ ...prev, strategies: !prev.strategies }))}
                >
                  <RailSectionLabel>Strategy Workbenches</RailSectionLabel>
                  <RailSectionMeta>
                    {strategyTabsForView.length}
                    <RailChevron $open={railOpen.strategies}><ChevronDown /></RailChevron>
                  </RailSectionMeta>
                </RailSectionHeader>
                <AnimatePresence initial={false}>
                  {railOpen.strategies && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.16 }}
                      style={{ overflow: 'hidden' }}
                    >
                      {renderRailItems(strategyTabsForView)}
                    </motion.div>
                  )}
                </AnimatePresence>
              </RailSection>

              <RailSection>
                <RailSectionHeader
                  type="button"
                  aria-expanded={railOpen.operations}
                  onClick={() => setRailOpen(prev => ({ ...prev, operations: !prev.operations }))}
                >
                  <RailSectionLabel>Lifecycle & Operations</RailSectionLabel>
                  <RailSectionMeta>
                    {operationsTabsForView.length}
                    <RailChevron $open={railOpen.operations}><ChevronDown /></RailChevron>
                  </RailSectionMeta>
                </RailSectionHeader>
                <AnimatePresence initial={false}>
                  {railOpen.operations && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.16 }}
                      style={{ overflow: 'hidden' }}
                    >
                      {renderRailItems(operationsTabsForView)}
                    </motion.div>
                  )}
                </AnimatePresence>
              </RailSection>
            </>
          )}
        </WorkspaceRail>

        <WorkspaceMain>
          <WorkspacePanel>
            <PanelHeader>
              <div>
                <PanelTitle>{activeMeta.label}</PanelTitle>
                <PanelSubtitle>{activeMeta.description} · {activeView.label}</PanelSubtitle>
              </div>
            </PanelHeader>
            <PanelBody>
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeSubTab}
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -5 }}
                  transition={{ duration: 0.15 }}
                >
                  {activeSubTab === 'overview' && <AlternativesOverview onJumpToSubTab={handleSubTabChange} />}
                  {activeSubTab === 'private-equity' && <PrivateAssetDashboard embedded scope="private-equity" />}
                  {activeSubTab === 'lp-performance' && <LPPerformanceCenterScreen />}
                  {activeSubTab === 'lp-fund-detail' && (
                    <LPFundPerformanceDetailScreen
                      embedded
                      fundIdOverride={searchParams.get('fundId') ?? undefined}
                    />
                  )}
                  {activeSubTab === 'pe-commitments' && <CommitmentDashboard embedded />}
                  {activeSubTab === 'pe-distributions' && <PEDistributionsPanel />}
                  {activeSubTab === 'lp-roster' && <GPLPRosterScreen embedded />}
                  {activeSubTab === 'fof-lookthrough' && <FoFLookthroughPanel />}
                  {activeSubTab === 'cashflow-forecast' && <CashFlowForecastScreen embedded />}
                  {activeSubTab === 'portfolio-companies' && <PortfolioCompaniesWorkbench />}
                  {activeSubTab === 'real-estate' && <RealEstateDashboard embedded />}
                  {activeSubTab === 'private-credit' && <PrivateCreditHome embedded />}
                  {activeSubTab === 're-debt' && <RealEstateDebtScreen embedded />}
                </motion.div>
              </AnimatePresence>
            </PanelBody>
          </WorkspacePanel>
        </WorkspaceMain>
      </WorkspaceBody>
    </WorkspaceShell>
  );
};

export default AlternativesTab;
